import { Box, Typography } from '@mui/material'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { invalidationActivityLogCache } from 'src/api/activity-log'
import { ShowDialogProps } from 'src/show-dialog'
import { ZodObject, z } from 'zod'
import { invalidateDeviation } from '../../api/deviation'
import {
  invalidateSchemaForm,
  useAddFormSchemaReport,
  useGetFormSchema,
  usePublishFormSchemaReport,
  useUpdateFormSchemaReport,
} from '../../api/form-schema'
import { invalidateLocalityFrequencyCache } from '../../api/locality-frequency'
import { FormFieldType } from '../../enums'
import {
  AUTO_FILL_DATE_TIME,
  AutoFillDateTimeFieldList,
  FormRow,
  getFieldKey,
} from '../../generated-types/form-field'
import {
  ICustomerFormSchema,
  IFormSchema,
  IFormSchemaReport,
  SchemaType,
  formSchemaReportTypes,
} from '../../generated-types/form-schema'
import { useDialogCtx } from '../../hooks/context-hooks'
import { GenericDialogLayout } from '../GenericDialogLayout'
import { FlexSpinner } from '../Spinner'
import DraftCaptionDialog from './DraftCaptionDialog'
import FormSchemaForm from './FormSchemaForm'
import { FormSchemasDialogPickerPanel } from './FormSchemasDialogPickerPanel'

// Same value formats as the date/time inputs in FieldSelector
const getCurrentDateTimeValue = (type: FormFieldType) => {
  switch (type) {
    case FormFieldType.datePicker:
      return moment().format('yyyy-MM-DD')
    case FormFieldType.timePicker:
      return moment().format('HH:mm')
    default:
      return new Date().toISOString()
  }
}

interface IFormSchemaDialog extends ShowDialogProps {
  customerId: EntityId
  localityId?: EntityId
  property: any
  formInitialValues: any
  selectedSchemaForm?: IFormSchema | null
  draftReports?: IFormSchemaReport[]
  selectedReportId?: number
}

const FormSchemasDialog: React.FC<IFormSchemaDialog> = ({
  customerId,
  localityId = null,
  pop,
  property,
  formInitialValues,
  selectedSchemaForm,
  draftReports,
  selectedReportId,
}) => {
  const { t } = useTranslation(['form-schema', 'form-schema-report', 'common', 'forms'])
  const { data, isLoading } = useGetFormSchema(customerId as EntityId, { schemaType: SchemaType.FormSchema })
  const { showDialog } = useDialogCtx()
  const mutate = useAddFormSchemaReport(customerId as EntityId)
  const [selectedSchema, setSelectedSchema] = useState<ICustomerFormSchema>()
  const [viewForm, setViewForm] = useState(false)
  const [schema, setSchema] = useState<ZodObject<any>>(z.object({}))
  const [initialValues, setInitialValues] = useState({})

  const [currentDraft, setCurrentDraft] = useState<IFormSchemaReport | undefined>()

  const draftId = currentDraft?.id
  const { mutateAsync: updateDraft } = useUpdateFormSchemaReport(customerId as EntityId, draftId as EntityId)
  const { mutateAsync: publishDraft } = usePublishFormSchemaReport(customerId as EntityId, draftId as EntityId)

  const invalidateReportsCache = () => {
    invalidateDeviation.getDeviationDocuments(customerId, property.deviationId)
    invalidateSchemaForm.getFilledFormSchemaReports(customerId)
    invalidateSchemaForm.getFormSchemaReports(
      customerId,
      property.type,
      property.localityFrequencyId,
      property.deviationId,
      property.mainComponentId,
      property.scheduledTaskId,
      property.localityId,
    )
  }

  const onSubmit = async form => {
    if (currentDraft) {
      await publishDraft(
        { values: JSON.stringify(form) },
        {
          onSuccess: () => {
            toast.success(t('form-schema-added-successfully', { ns: 'form-schema-report' }))
          },
        },
      )
    } else {
      const formSchemaReport: IFormSchemaReport = {
        formSchemaVersionId: selectedSchema?.version?.id,
        values: JSON.stringify(form),
        type: property.type,
        deviationId: property.deviationId,
        localityFrequencyId: property.localityFrequencyId,
        groupId: property.groupId,
        mainComponentId: property.mainComponentId,
        scheduledTaskId: property.scheduledTaskId,
        localityId: property.localityId != null ? Number(property.localityId) : undefined,
      }
      await mutate.mutateAsync(formSchemaReport, {
        onSuccess: () => {
          toast.success(t('form-schema-added-successfully', { ns: 'form-schema-report' }))
        },
      })
    }

    invalidateReportsCache()
    invalidationActivityLogCache.getCageActivityLogs()

    if (property.deviationId) {
      invalidateDeviation.getDeviationDocuments(customerId, property.deviationId)
    }
    if (localityId && (property.localityFrequencyId || property.groupId)) {
      invalidateLocalityFrequencyCache.getAllLocalityFrequencies(customerId as EntityId, localityId as EntityId)
    }
    if (property.type === formSchemaReportTypes.Locality && property.localityId != null) {
      invalidateSchemaForm.getFilledFormSchemaReports(customerId as EntityId, {
        localityId: Number(property.localityId),
      })
    }

    pop()
  }

  const onSaveDraft = (values: any) => {
    showDialog(
      DraftCaptionDialog,
      {
        componentProps: { initialCaption: currentDraft?.draftCaption ?? '' },
        pop: async (caption?: string) => {
          if (caption === undefined) return
          if (currentDraft) {
            await updateDraft({ values: JSON.stringify(values), draftCaption: caption })
          } else {
            const formSchemaReport: IFormSchemaReport = {
              formSchemaVersionId: selectedSchema?.version.id,
              values: JSON.stringify(values),
              type: property.type,
              deviationId: property.deviationId,
              localityFrequencyId: property.localityFrequencyId,
              groupId: property.groupId,
              mainComponentId: property.mainComponentId,
              scheduledTaskId: property.scheduledTaskId,
              localityId: property.localityId != null ? Number(property.localityId) : undefined,
              isDraft: true,
              draftCaption: caption,
            }
            const newDraft = await mutate.mutateAsync(formSchemaReport)
            if (newDraft?.data?.id) {
              setCurrentDraft(newDraft.data)
            }
          }
          toast.success(t('draft-saved-successfully', { ns: 'form-schema-report' }))
          invalidateReportsCache()
          pop()
        },
        opts: { maxWidth: 'sm', fullWidth: true },
      },
      undefined,
      true,
    )
  }

  const openFormSchema = (form: ICustomerFormSchema) => {
    let schemaObj = z.object({})
    const layouts: FormRow[] = JSON.parse(form.version?.layout)
    const initialValueObject = {}
    layouts.forEach(row => {
      row.columns?.forEach(col => {
        col?.fields.forEach(field => {
          const key = getFieldKey(field)
          if (field.type != FormFieldType.title) {
            if (AutoFillDateTimeFieldList.includes(field.type)) {
              initialValueObject[key] =
                field.defaultValue === AUTO_FILL_DATE_TIME ? getCurrentDateTimeValue(field.type) : ''
            } else if (
              field.defaultValue !== undefined &&
              (formInitialValues[field.defaultValue] == undefined || formInitialValues[field.defaultValue] == 'n/a')
            ) {
              initialValueObject[key] = ''
              field.disabled = field.disabled ?? false
            } else {
              initialValueObject[key] = formInitialValues[field.defaultValue || '']
              field.disabled = field.disabled ?? false
            }
          }
          if (field.isRequired) {
            schemaObj = schemaObj.merge(
              z.object({
                [key]: z.string().min(1, { message: t('requiredField', { ns: 'forms' }).replace('{0}', field.name) }),
              }),
            )
          } else {
            schemaObj = schemaObj.merge(z.object({ [key]: z.string().optional() }))
          }
        })
      })
    })

    if (selectedReportId != null) {
      const draft = draftReports?.find(d => d.id == selectedReportId)
      if (draft?.values) {
        const draftValues = JSON.parse(draft.values)
        layouts.forEach(row => {
          row.columns?.forEach(col => {
            col?.fields.forEach(field => {
              const key = getFieldKey(field)
              const savedValue = draftValues[key] ?? draftValues[field.name]
              if (savedValue !== undefined) {
                initialValueObject[key] = savedValue
              }
            })
          })
        })
      }
      setCurrentDraft(draft)
    }

    if (form?.version) {
      form.version.layout = JSON.stringify(layouts)
    }
    setInitialValues(initialValueObject)
    setSchema(schemaObj)
    setSelectedSchema(form)
  }

  useEffect(() => {
    if (selectedSchemaForm && data) {
      const form = data.find(f => f.id == selectedSchemaForm.id)
      if (form) {
        openFormSchema(form)
      }
    }
  }, [selectedSchemaForm, data])

  useEffect(() => {
    if (schema && selectedSchema) {
      setViewForm(true)
    }
  }, [schema])

  return (
    <>
      {isLoading ? (
        <FlexSpinner />
      ) : (
        <>
          {!viewForm ? (
            <GenericDialogLayout
              title={
                <Box sx={{ pr: 1 }}>
                  <Typography variant="h6" fontWeight={600} color="text.primary">
                    {t('your-forms', { ns: 'form-schema' })}
                  </Typography>
                </Box>
              }
              titleSectionTopPadding="16px"
              titleSectionBottomPadding="8px"
              titleSectionXPadding="20px"
              titleDivider
              removedCloseButton={Boolean(selectedSchema)}
              pop={pop}
              sx={{ px: 2, pt: 1, pb: 2 }}
            >
              <FormSchemasDialogPickerPanel data={data} onSelectForm={openFormSchema} />
            </GenericDialogLayout>
          ) : (
            selectedSchema &&
            schema && (
              <FormSchemaForm
                schema={schema}
                version={selectedSchema?.version}
                title={selectedSchema?.name}
                initialValues={initialValues}
                pop={pop}
                onSubmit={onSubmit}
                onSaveDraft={onSaveDraft}
                isDraft={currentDraft?.isDraft}
              />
            )
          )}
        </>
      )}
    </>
  )
}

export default FormSchemasDialog
